import { useEffect, useState } from "react";
import { Header } from "@/components/Header";
import { api } from "@/lib/api";
import { Search, BookOpen, ChevronDown } from "lucide-react";

const CATEGORY_VISUALS = {
  GVO: { color: "#34D399", glyph: "✉" },
  ManyChat: { color: "#D4AF37", glyph: "◆" },
  Marketing: { color: "#FBBF24", glyph: "✷" },
};

export default function KnowledgeBase() {
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [data, setData] = useState({ results: [], categories: [] });
  const [openIds, setOpenIds] = useState({});

  const fetchData = async () => {
    const params = new URLSearchParams();
    if (q) params.append("q", q);
    if (category && category !== "all") params.append("category", category);
    const { data } = await api.get(`/knowledge?${params.toString()}`);
    setData(data);
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [q, category]);

  const toggle = (id) => setOpenIds((o) => ({ ...o, [id]: !o[id] }));

  return (
    <div className="min-h-screen bg-[#060B14]">
      <Header />

      <main className="max-w-5xl mx-auto px-6 lg:px-10 py-10">
        <div className="flex items-center gap-3 mb-4">
          <div className="hud-line w-10" />
          <span className="hud-label">Field Manual</span>
        </div>
        <h1 className="font-display text-3xl sm:text-4xl text-white font-light mb-3">
          Illustrated <span className="text-gold italic">Canned Answers</span>
        </h1>
        <p className="text-slate-400 text-sm mb-10 max-w-2xl">
          Searchable playbook for GVO, ManyChat, and Marketing fundamentals. Use it mid-flight.
        </p>

        {/* Search bar */}
        <div className="glass rounded-sm p-2 flex flex-col sm:flex-row gap-2 mb-6">
          <div className="flex-1 flex items-center gap-3 px-4 bg-[#060B14] rounded-sm border border-white/10 focus-within:border-gold transition-colors">
            <Search size={16} className="text-slate-500" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search the field manual..."
              data-testid="knowledge-search-input"
              className="flex-1 bg-transparent py-3 text-white placeholder-slate-600 focus:outline-none"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            <CategoryChip label="All" value="all" current={category} onClick={setCategory} testId="category-all" />
            {data.categories.map((c) => (
              <CategoryChip key={c} label={c} value={c} current={category} onClick={setCategory} testId={`category-${c.toLowerCase()}`} />
            ))}
          </div>
        </div>

        <div className="text-xs text-slate-500 mb-4 font-mono-aviation">
          {data.results.length} {data.results.length === 1 ? "answer" : "answers"} ready
        </div>

        <div className="space-y-3" data-testid="knowledge-results">
          {data.results.map((a) => {
            const vis = CATEGORY_VISUALS[a.category] || { color: "#94A3B8", glyph: "·" };
            const open = !!openIds[a.id];
            return (
              <div key={a.id} className="border border-white/10 rounded-sm hover:border-gold/40 transition-colors" data-testid={`knowledge-item-${a.id}`}>
                <button
                  onClick={() => toggle(a.id)}
                  data-testid={`knowledge-toggle-${a.id}`}
                  className="w-full text-left p-5 flex items-start gap-4"
                >
                  <div
                    className="w-10 h-10 rounded-sm border flex items-center justify-center text-lg shrink-0"
                    style={{ borderColor: `${vis.color}40`, color: vis.color }}
                  >
                    {vis.glyph}
                  </div>
                  <div className="flex-1">
                    <div className="hud-label text-[9px] mb-1" style={{ color: vis.color }}>{a.category}</div>
                    <div className="font-display text-base sm:text-lg text-white">{a.title}</div>
                    {open && (
                      <div className="mt-4 text-sm text-slate-300 leading-relaxed">{a.answer}</div>
                    )}
                    {open && (
                      <div className="flex flex-wrap gap-2 mt-4">
                        {a.tags.map((t) => (
                          <span key={t} className="text-[10px] uppercase tracking-wider text-slate-500 border border-white/10 rounded-sm px-2 py-1">
                            #{t}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <ChevronDown
                    size={18}
                    className={`text-slate-500 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
                  />
                </button>
              </div>
            );
          })}
          {data.results.length === 0 && (
            <div className="p-12 text-center text-slate-500 border border-white/10 rounded-sm" data-testid="knowledge-empty">
              <BookOpen size={28} className="mx-auto mb-3 text-slate-600" />
              No matches. Try a different keyword or category.
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

const CategoryChip = ({ label, value, current, onClick, testId }) => {
  const active = current === value;
  return (
    <button
      onClick={() => onClick(value)}
      data-testid={testId}
      className={`px-4 py-2 rounded-sm text-xs uppercase tracking-wider transition-colors ${
        active
          ? "bg-gold text-[#060B14] font-semibold"
          : "border border-white/10 text-slate-400 hover:border-gold hover:text-gold"
      }`}
    >
      {label}
    </button>
  );
};
